<template>
    <div class="weeklyTarget-wrapper wtpol-page">
        <div class="d-flex justify-content-center align-items-center weeklyTarget-header text-center">
            <span class="backArrowIcon" id="close" @click="goBack('someStatus')">
                <img :src="downArrow" alt="Back Arrow" />
            </span>
            <h2 class="m-0">Rules &amp; Regulations</h2>
        </div>

        <div class="wtpol-inner">

            <!-- Language switch -->
            <div class="wtpol-seg">
                <button type="button" :class="{ 'is-on': activeTab === 'language1Tab' }"
                    @click="activateTab('language1Tab')">Hinglish</button>
                <button type="button" :class="{ 'is-on': activeTab === 'language2Tab' }"
                    @click="activateTab('language2Tab')">English</button>
            </div>

            <!-- General streaming -->
            <section class="wtpol-card">
                <div class="wtpol-cardHd">
                    <span class="wtpol-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F2820D" stroke-width="2.1"
                            stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M12 7v5.2l3.2 1.9" />
                        </svg>
                    </span>
                    <h3>{{ copy.generalTitle }}</h3>
                </div>
                <ul class="wtpol-list">
                    <li v-for="(rule, i) in copy.general" :key="i" v-html="rule"></li>
                </ul>
            </section>

            <!-- Peak hour bonus — the 42 hrs programme's extra window.
                 The old markup hung this on `hidePeakHourBoxAfterTargetTime`,
                 a property that was never declared, so the condition was
                 always true and the block always showed. Rendering it
                 unconditionally keeps that behaviour without the stray
                 reference. -->
            <section class="wtpol-card is-peak">
                <div class="wtpol-cardHd">
                    <span class="wtpol-icon is-purple">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7C40FF" stroke-width="2.1"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M13 2.5L4.5 13.5H11l-1 8 8.5-11H12l1-7.5z" />
                        </svg>
                    </span>
                    <h3>{{ copy.peakTitle }}</h3>
                </div>
                <ul class="wtpol-list">
                    <li v-for="(rule, i) in copy.peak" :key="i" v-html="rule"></li>
                </ul>
            </section>

            <!-- Guidelines -->
            <section class="wtpol-card">
                <div class="wtpol-cardHd">
                    <span class="wtpol-icon is-blue">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2F6FE4" stroke-width="2.1"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M3.5 6.5h13v11h-13z" />
                            <path d="M16.5 11l4-2.5v7l-4-2.5z" />
                        </svg>
                    </span>
                    <h3>{{ copy.guidelinesTitle }}</h3>
                </div>
                <ol class="wtpol-steps">
                    <li v-for="(guide, i) in copy.guidelines" :key="i">{{ guide }}</li>
                </ol>
            </section>

            <!-- The Earn According to Target ladder and the Note block were
                 commented out of this page before the redesign, so they stay
                 out. To bring them back, copy the ladder markup from
                 WeeklyTargetPolicy.vue — the styles are already in the shared
                 stylesheet — with this page's own figures:
                 100K/40K, 250K/90K, 500K/170K, 1M/330K, 2.5M/825K, 5M/1.65M. -->

        </div>
    </div>
</template>

<script>
import downArrow from '../../assets/images/back-blackArrow.svg';
import './WeeklyTargetPolicyStyle.css';

// One entry per language so the two tabs cannot drift apart the way two
// duplicated markup blocks did.
const COPY = {
    language1Tab: {
        generalTitle: 'General Streaming',
        general: [
            'Streaming 24/7 available hai.',
            'Hosts ko roz maximum <b>9 hrs</b> stream aur calls milakar count hoga.',
            'Rewards ke liye, har week (Monday se Sunday) total <b>42 hrs</b> ka live streaming karna zaroori hai.',
            'Rewards ke liye, ek week me minimum <b>200,000 beans</b> earn karna hoga.',
        ],
        peakTitle: 'Peak Hour Bonus',
        peak: [
            'Valid streaming hours: <b>Raat 8 baje se subah 2 baje tak.</b>',
            'Is samay ke dauraan roz maximum <b>4 hrs</b> stream aur calls milakar count hoga.',
            'Rewards ke liye, har week (Monday se Sunday) total <b>21 hrs</b> ka live streaming karna zaroori hai.',
        ],
        guidelinesTitle: 'Livestreaming Guidelines',
        guidelines: [
            'Apka background saaf-suthra aur organized hona chahiye. Professional look ke liye ek solid ya visually appealing background color use karein.',
            'Clear audio ke liye Bluetooth devices ka use karein aur visual effects ko enhance karne ke liye ring light lagayen.',
            'Smart casuals pehniye taaki aapki appearance polished aur professional lage.',
        ],
    },

    language2Tab: {
        generalTitle: 'General Streaming',
        general: [
            'Streaming is available 24/7.',
            'Hosts must stream and engage in calls for a maximum of <b>9 hours</b> daily.',
            'To qualify for rewards, a total of <b>42 hours</b> of live streaming must be completed weekly (Monday to Sunday).',
            'A minimum of <b>200,000 beans</b> must be earned per week to be eligible for rewards.',
        ],
        peakTitle: 'Peak Hour Bonus',
        peak: [
            'Valid streaming hours: <b>8:00 PM - 2:00 AM</b>',
            'Hosts must stream and engage in calls for a maximum of <b>4 hours</b> daily during this time.',
            'To qualify for rewards, a total of <b>21 hours</b> of live streaming must be completed weekly (Monday to Sunday).',
        ],
        guidelinesTitle: 'Livestreaming Guidelines',
        guidelines: [
            'Ensure your background is tidy and well-organized. Consider using a solid or visually appealing background color for a professional look.',
            'Use Bluetooth devices for clear audio quality and a ring light to enhance visual effects and presentation.',
            'Dress in smart casual attire to maintain a polished and professional appearance.',
        ],
    },
};

export default {
    name: 'WeeklyTargetPolicy_status3',
    data() {
        return {
            activeTab: 'language1Tab',
            downArrow,
        };
    },

    computed: {
        copy() {
            return COPY[this.activeTab] || COPY.language1Tab;
        },
    },

    methods: {
        activateTab(tabId) {
            this.activeTab = tabId;
        },
        handleTabChange(tabId) {
            this.activeTab = tabId;
        },

        // Close Webview on back arrow click
        goBack() {
            this.$router.go(-1);
        },
    },
};
</script>
